<template>
  <div class="tin-page">
    <template v-if="verified">
    <div class="ambient"></div>
    <div class="grain"></div>
    <div class="frame"></div>
    <div class="frame-inner"></div>

    <div class="page-inner">
      <div class="wm-row slim">
        <div class="wm-line"></div>
        <span class="wm-text">Club Trailz · Admin</span>
        <div class="wm-line r"></div>
      </div>

      <div class="header-row">
        <h1 class="page-title left">Welcome, <em>{{ firstName }}</em></h1>
        <button class="gear-btn" @click="$router.push({ name: 'AdminSettings' })">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
        </button>
      </div>

      <!-- Stat -->
      <button class="stat-card" @click="$router.push({ name: 'AdminWaitlist' })">
        <div>
          <p class="stat-number">{{ pendingCount }}</p>
          <p class="stat-label">Pending Applications</p>
        </div>
        <div class="stat-side">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          <p v-if="hasMissingClub" class="stat-flag">SOME MISSING CLUB INFO</p>
        </div>
      </button>

      <!-- Platform access -->
      <div class="section-hd">
        <div class="section-rule l"></div>
        <span class="section-eyebrow">Platform</span>
        <div class="section-rule"></div>
      </div>
      <h2 class="section-title">Full <em>Access</em></h2>
      <div class="tile-grid">
        <button class="tile" @click="$router.push('/explore')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 2l2.4 7.2H22l-6 4.6 2.3 7.2-6.3-4.6-6.3 4.6 2.3-7.2-6-4.6h7.6z"/></svg>
          <span>Explore</span>
        </button>
        <button class="tile" @click="$router.push('/groups')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
          <span>Chapters</span>
        </button>
        <button class="tile" @click="$router.push('/messages')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/></svg>
          <span>Messages</span>
        </button>
        <button class="tile" @click="$router.push('/search')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          <span>My Network</span>
        </button>
      </div>

      <!-- Recent applications -->
      <div class="section-hd">
        <div class="section-rule l"></div>
        <span class="section-eyebrow">Review Queue</span>
        <div class="section-rule"></div>
      </div>
      <h2 class="section-title">Recent <em>Applications</em></h2>

      <div v-if="loading" class="empty-block"><p class="empty-text">Loading…</p></div>
      <div v-else-if="!recent.length" class="empty-block"><p class="empty-text">No pending applications.</p></div>
      <div v-else class="recent-list">
        <button v-for="app in recent" :key="app.uid" class="recent-row" @click="$router.push({ name: 'AdminWaitlist' })">
          <div class="app-avatar">
            <img v-if="app.image" :src="app.image" alt="" />
            <span v-else>{{ initials(app.fullName) }}</span>
          </div>
          <div class="recent-info">
            <p class="app-name">{{ app.fullName }}</p>
            <p class="app-club" :class="{ 'app-club--missing': !app.primaryClub }">{{ app.primaryClub || 'No club listed' }}</p>
          </div>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
        <button v-if="pendingCount > 3" class="view-all-btn" @click="$router.push({ name: 'AdminWaitlist' })">
          VIEW ALL {{ pendingCount }} →
        </button>
      </div>
        <!-- Bottom Tab Bar -->
    <div class="admin-tab-bar">
      <button class="tab-item" :class="{ 'tab-item--active': $route.name === 'AdminHomeView' }" @click="$router.push({ name: 'AdminHomeView' })">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        <span>Home</span>
      </button>
      <button class="tab-item" :class="{ 'tab-item--active': $route.name === 'AdminSearch' }" @click="$router.push({ name: 'AdminSearch' })">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <span>Search</span>
      </button>
      <button class="tab-item" :class="{ 'tab-item--active': $route.name === 'AdminWaitlist' }" @click="$router.push({ name: 'AdminWaitlist' })">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
        <span>Waitlist</span>
      </button>
      <button class="tab-item" :class="{ 'tab-item--active': $route.name === 'AdminFlags' }" @click="$router.push({ name: 'AdminFlags' })">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
        <span>Flags</span>
      </button>
      <button class="tab-item" :class="{ 'tab-item--active': $route.name === 'AdminMessages' }" @click="$router.push({ name: 'AdminMessages' })">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/></svg>
        <span>Messages</span>
      </button>
      <button class="tab-item" :class="{ 'tab-item--active': $route.name === 'AdminSettings' }" @click="$router.push({ name: 'AdminSettings' })">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
        <span>Settings</span>
      </button>
    </div>

  </div>
    </template>
    <div v-else class="admin-check-loading"></div>
  </div>
</template>

<script>
import { collection, query, where, orderBy, onSnapshot, doc, getDoc } from "firebase/firestore";
import { db, auth } from "@/firebase";

// Replaces the old "Welcome, Admin!" page, which only ever linked to
// /member-applications and showed nothing else — an admin is still a
// member first, so this gives full platform access (same as any member's
// home) plus admin-specific stats, instead of a dead end.
export default {
  name: "AdminHomeView",
  data() {
    return {
      loading: true,
      pending: [],
      unsub: null,
      adminName: auth.currentUser?.displayName || "",
      // Component-level defense in depth. The router guard in index.js's
      // beforeEach already checks meta.requiresAdmin before ever navigating
      // here, but that's the only line of defense if this check doesn't
      // also exist on the component itself — any future route, redirect,
      // or programmatic navigation that reaches this component without
      // going through that guard would otherwise render the full admin
      // shell, stats, and navigation with no check at all. Real admin
      // data stays protected by Firestore rules either way, but the UI
      // itself shouldn't be reachable by a non-admin even for a moment.
      verified: false,
    };
  },
  computed: {
    firstName() { return (this.adminName || "Admin").split(" ")[0]; },
    pendingCount() { return this.pending.length; },
    recent() { return this.pending.slice(0, 3); },
    hasMissingClub() { return this.pending.some(a => !a.primaryClub); },
  },
  async created() {
    const user = auth.currentUser;
    if (!user) { this.$router.replace("/"); return; }
    try {
      const snap = await getDoc(doc(db, "user", user.uid));
      const userType = snap.exists() ? (snap.data().type || snap.data().role) : null;
      if (userType !== "Admin") { this.$router.replace("/home"); return; }
      this.verified = true;
      this.startListening();
    } catch (e) {
      this.$router.replace("/home");
    }
  },
  beforeUnmount() { if (this.unsub) this.unsub(); },
  methods: {
    startListening() {
      const qy = query(collection(db, "applications"), where("status", "==", "pending_review"), orderBy("createdAt", "desc"));
      this.unsub = onSnapshot(qy, (snap) => {
        this.pending = snap.docs.map(d => {
          const data = d.data();
          const clubs = Array.isArray(data.clubs) ? data.clubs.filter(c => c && c.name) : [];
          return { uid: d.id, fullName: data.fullName || "", image: data.image || null, primaryClub: clubs[0]?.name || "" };
        });
        this.loading = false;
      }, () => { this.loading = false; });
    },
    initials(name) {
      const n = (name || "").trim();
      if (!n) return "?";
      return n.split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() || "").join("");
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');
.tin-page { position: relative; min-height: 100vh; background: #070708; }
.ambient { position: fixed; top: -300px; left: 50%; transform: translateX(-50%); width: 1000px; height: 800px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 10%, rgba(210,175,100,0.1) 0%, rgba(210,175,100,0.03) 30%, transparent 65%); }
.grain { position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: 0.5; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.12'/%3E%3C/svg%3E"); background-size: 180px 180px; }
.frame { position: fixed; inset: 28px; border: 1px solid rgba(210,175,100,0.08); z-index: 2; pointer-events: none; }
.frame::before, .frame::after { content: ''; position: absolute; width: 20px; height: 20px; }
.frame::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.frame::after { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner { position: fixed; inset: 28px; z-index: 2; pointer-events: none; }
.frame-inner::before, .frame-inner::after { content: ''; position: absolute; width: 20px; height: 20px; }
.frame-inner::before { top: -1px; right: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner::after { bottom: -1px; left: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.page-inner { position: relative; z-index: 10; max-width: 780px; margin: 0 auto; padding: 60px 40px 100px; }
.wm-row { display: flex; align-items: center; gap: 14px; }
.wm-row.slim { margin-bottom: 20px; }
.wm-line { flex: 1; height: 1px; background: linear-gradient(90deg, transparent, rgba(210,175,100,0.2)); }
.wm-line.r { background: linear-gradient(90deg, rgba(210,175,100,0.2), transparent); }
.wm-text { font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.5); text-transform: uppercase; }

.header-row { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 28px; }
.page-title { font-family: 'Cormorant', serif; font-size: 34px; font-weight: 300; color: #edeae2; }
.page-title em { font-style: italic; color: rgba(210,175,100,0.8); }
.gear-btn { background: none; border: 1px solid rgba(210,175,100,0.25); width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: rgba(210,175,100,0.5); cursor: pointer; }

.stat-card { display: flex; align-items: center; justify-content: space-between; width: 100%; background: rgba(210,175,100,0.06); border: 1px solid rgba(210,175,100,0.3); padding: 24px; cursor: pointer; margin-bottom: 40px; text-align: left; }
.stat-number { font-family: 'Cormorant', serif; font-size: 42px; color: rgba(210,175,100,0.85); }
.stat-label { font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 2.5px; color: rgba(237,232,223,0.35); text-transform: uppercase; margin-top: 4px; }
.stat-side { display: flex; flex-direction: column; align-items: flex-end; gap: 8px; color: rgba(210,175,100,0.4); }
.stat-flag { font-family: 'Montserrat', sans-serif; font-size: 8px; font-weight: 600; letter-spacing: 1px; color: rgba(220,100,90,0.6); }

.section-hd { display: flex; align-items: center; gap: 14px; margin-bottom: 8px; }
.section-rule { flex: 1; height: 1px; background: linear-gradient(90deg, transparent, rgba(210,175,100,0.18)); }
.section-rule.l { background: linear-gradient(90deg, rgba(210,175,100,0.18), transparent); }
.section-eyebrow { font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.4); text-transform: uppercase; white-space: nowrap; }
.section-title { font-family: 'Cormorant', serif; font-size: 24px; font-weight: 300; color: #edeae2; margin-bottom: 16px; }
.section-title em { font-style: italic; color: rgba(210,175,100,0.75); }

.tile-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 40px; }
.tile { display: flex; flex-direction: column; align-items: center; gap: 10px; background: rgba(210,175,100,0.04); border: 1px solid rgba(210,175,100,0.15); padding: 26px 12px; cursor: pointer; color: rgba(210,175,100,0.6); }
.tile span { font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; letter-spacing: 1.5px; color: rgba(237,232,223,0.6); text-transform: uppercase; }

.empty-block { padding: 20px 0; }
.empty-text { font-family: 'Montserrat', sans-serif; font-size: 12px; color: rgba(237,232,223,0.2); }

.recent-list { display: flex; flex-direction: column; gap: 10px; }
.recent-row { display: flex; align-items: center; gap: 14px; background: rgba(210,175,100,0.03); border: 1px solid rgba(210,175,100,0.1); padding: 14px; cursor: pointer; text-align: left; color: rgba(210,175,100,0.3); }
.app-avatar { width: 40px; height: 40px; border-radius: 50%; border: 1px solid rgba(210,175,100,0.25); display: flex; align-items: center; justify-content: center; overflow: hidden; flex-shrink: 0; font-family: 'Cormorant', serif; font-size: 14px; color: rgba(210,175,100,0.5); }
.app-avatar img { width: 100%; height: 100%; object-fit: cover; }
.recent-info { flex: 1; min-width: 0; }
.app-name { font-family: 'Cormorant', serif; font-size: 15px; color: #edeae2; }
.app-club { font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 1px; color: rgba(210,175,100,0.4); }
.app-club--missing { color: rgba(220,100,90,0.6); }
.view-all-btn { background: none; border: none; font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 2px; color: rgba(210,175,100,0.55); cursor: pointer; padding-top: 8px; }

/* ── ADMIN TAB BAR ── */
.admin-tab-bar { position: fixed; bottom: 0; left: 0; right: 0; z-index: 50; display: flex; background: rgba(7,7,8,0.97); border-top: 1px solid rgba(210,175,100,0.12); padding: 10px 0 max(10px, env(safe-area-inset-bottom)); }
.tab-item { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 5px; background: none; border: none; cursor: pointer; color: rgba(237,232,223,0.25); padding: 6px 0; }
.tab-item span { font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; }
.tab-item--active { color: rgba(210,175,100,0.85); }

</style>